/** Backend cap on product ids per progress request. */
export const LIBRARY_PROGRESS_MAX_BATCH = 50

/** The parts of a library row the progress call needs. */
export interface LibraryProgressSource {
  id: string
  membershipUserId?: string | null
}

/** One progress request: every id belongs to the same membership user. */
export interface LibraryProgressBatch {
  /** Sent as `x-product-id` so the client-portal proxy can resolve which learner is asking. */
  anchorProductId: string
  productIds: string[]
}

const chunk = (ids: string[], size: number): string[][] =>
  Array.from({ length: Math.ceil(ids.length / size) }, (_, index) => ids.slice(index * size, (index + 1) * size))

/**
 * Split library rows into progress requests, one membership user per request.
 *
 * The progress endpoint counts lessons for a single learner, and the proxy picks that learner
 * from the product in `x-product-id`. A learner can own library rows through several membership
 * users, so each user gets its own request. Rows without a `membershipUserId` go alone, anchored
 * on themselves. The anchor prefers a product that no other group holds, so the proxy cannot
 * resolve it to a different membership user.
 */
export const buildLibraryProgressBatches = (products: readonly LibraryProgressSource[]): LibraryProgressBatch[] => {
  const idsByGroup = products.reduce<Map<string, Set<string>>>((groups, product) => {
    if (!product.id) return groups
    const groupKey = product.membershipUserId || `product:${product.id}`
    const ids = groups.get(groupKey) ?? new Set<string>()
    ids.add(product.id)
    return groups.set(groupKey, ids)
  }, new Map())

  const groupCountByProductId = new Map<string, number>()
  idsByGroup.forEach(ids => ids.forEach(id => groupCountByProductId.set(id, (groupCountByProductId.get(id) ?? 0) + 1)))

  return [...idsByGroup.keys()].sort().flatMap(groupKey => {
    const ids = [...(idsByGroup.get(groupKey) ?? [])].sort()
    return chunk(ids, LIBRARY_PROGRESS_MAX_BATCH).map(productIds => ({
      anchorProductId: productIds.find(id => groupCountByProductId.get(id) === 1) ?? productIds[0],
      productIds,
    }))
  })
}
